import {
  Flex,
  Stack,
  Grid,
  Box,
  Text,
  Button,
  Textarea,
  useDisclosure,
  useTheme,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Image,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import igStore1 from 'assets/ig_store_1.webp'
import igStore2 from 'assets/ig_store_2.webp'
import igBusiness1 from 'assets/ig_business_1.webp'
import igBusiness2 from 'assets/ig_business_2.webp'
import igStreet1 from 'assets/ig_street_1.webp'
import igStreet2 from 'assets/ig_street_2.webp'
import igOval1 from 'assets/ig_oval_1.webp'
import igOval2 from 'assets/ig_oval_2.webp'
import igInterview from 'assets/ig_interview.webp'
import igRestaurant1 from 'assets/ig_restaurant_1.webp'
import igRestaurant2 from 'assets/ig_restaurant_2.webp'
import igRestaurant3 from 'assets/ig_restaurant_3.webp'
import igBeach1 from 'assets/ig_beach_1.webp'
import igBeach2 from 'assets/ig_beach_2.webp'

export default function ImageGeneration() {
  const theme = useTheme()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [prompt, setPrompt] = useState('')
  const [generatedImages, setGeneratedImages] = useState([
    { id: '1', src: igOval1, label: 'Oval setting' },
    { id: '2', src: igOval2, label: 'Oval setting' },
    { id: '3', src: igInterview, label: 'Interview setting' },
    { id: '4', src: igRestaurant1, label: 'Restaurant setting' },
    { id: '5', src: igRestaurant2, label: 'Restaurant setting' },
    { id: '6', src: igRestaurant3, label: 'Restaurant setting' },
    { id: '7', src: igBeach1, label: 'Beach setting' },
    { id: '8', src: igBeach2, label: 'Beach setting' },
  ])
  const [selectedImage, setSelectedImage] = useState(null)
  const navigate = useNavigate()

  const isMobile = useBreakpointValue({ base: true, md: false })

  const handleGenerate = () => {
    const imageSets = {
      business: [
        { id: '9', src: igBusiness1, label: 'Business setting' },
        { id: '10', src: igBusiness2, label: 'Business setting' },
      ],
      sportsStore: [
        { id: '11', src: igStore1, label: 'Sports store setting' },
        { id: '12', src: igStore2, label: 'Sports store setting' },
      ],
      street: [
        { id: '13', src: igStreet1, label: 'Street setting' },
        { id: '14', src: igStreet2, label: 'Street setting' },
      ],
    }

    let newImages = []
    if (prompt.toLowerCase().includes('business')) {
      newImages = imageSets.business
    } else if (prompt.toLowerCase().includes('sports store')) {
      newImages = imageSets.sportsStore
    } else if (prompt.toLowerCase().includes('street')) {
      newImages = imageSets.street
    }

    setGeneratedImages(prevImages => [...newImages, ...prevImages])
  }

  const handleImageClick = image => {
    setSelectedImage(image.src)
    onOpen()
  }

  const handleGenerateVideo = (imageId, imageSrc) => {
    navigate('/create-video', { state: { imageId, imageSrc } })
  }

  return (
    <>
      <Flex w="100%" h="100%" mb={8} direction="column" align="center">
        <Stack
          w="100%"
          direction="column"
          align="flex-start"
          spacing={4}
          mx={{ base: 4, xl: 16 }}
          maxW={{ base: '100%', xl: '1200px' }}
        >
          <Flex
            w="100%"
            borderRadius="5px"
            px={{ base: 4, lg: 0 }}
            py={{ base: 4, lg: 2 }}
            backgroundColor={theme.baseColors.background.default}
            flexDirection="column"
          >
            <Text as="h1" fontSize="2xl" fontWeight="bold" mb={2}>
              Image Generation
            </Text>
          </Flex>

          <Flex w="100%" direction={isMobile ? 'column' : 'row'} align="flex-start" p={8}>
            <Box
              w={isMobile ? '100%' : '30%'}
              p={4}
              borderRadius="8px"
              bg={theme.baseColors.background.default}
              mr={isMobile ? 0 : 8}
              mb={isMobile ? 4 : 0}
            >
              <Textarea
                placeholder="Describe the setting. E.g. Avatar in a sports store, street, business meeting."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault() // Prevents new line on Enter key
                    handleGenerate()
                  }
                }}
                mb={4}
                size="lg"
                resize="vertical"
                height="100px"
              />
              <Button colorScheme="purple" onClick={handleGenerate} w="100%">
                Generate
              </Button>
            </Box>

            <Box w={isMobile ? '100%' : '70%'}>
              <Grid templateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={4}>
                {generatedImages.map(image => (
                  <Box
                    key={image.id}
                    overflow="hidden"
                    boxShadow="md"
                    bg="white"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    onClick={() => handleImageClick(image)}
                    cursor="pointer"
                  >
                    <Box
                      h="150px"
                      w="150px" // Forces a square shape
                      overflow="hidden"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Image
                        src={image.src}
                        alt={image.label}
                        objectFit="cover"
                        w="100%"
                        h="100%"
                      />
                    </Box>
                    <Text p={2} fontSize="sm" textAlign="center">
                      {image.label}
                    </Text>
                    <Button
                      colorScheme="purple"
                      mt={2}
                      w="100%"
                      onClick={() => handleGenerateVideo(image.id, image.src)}
                    >
                      Create Video
                    </Button>
                  </Box>
                ))}
              </Grid>
            </Box>
          </Flex>
        </Stack>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent maxW="500px" maxH="500px" overflow="hidden">
          <ModalBody p={0}>
            {selectedImage && (
              <Image
                src={selectedImage}
                alt="Selected"
                objectFit="cover"
                width="100%"
                height="100%"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
